import * as React from 'react';

import { AnyCalendarDate } from '@internationalized/date';
import {
  PurchaseOrderStatus,
  enumPurchaseOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridColumn, LocalizedDate, Button, Menu } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideMoreHorizontal } from 'lucide-react';

import { PurchaseOrderRecord } from '../../../types/purchaseOrder';
import { PurchaseOrderStatusBadge } from '../../_shared/StatusBadges';
import { CreatedOnDateRangeColumnFilter } from '../filters/CreatedOnDateRangeColumnFilter';
import { DateRangeColumnFilter } from '../filters/DateRangeColumnFilter';
import { StatusColumnFilter } from '../filters/StatusColumnFilter';
import { NetWeightCell } from '../NetWeightCell';

import { usePurchaseOrderDetailMenuItems } from './usePurchaseOrderDetailMenuItems';

import { FacilityRecord } from '~/modules/trade/_graphql/queryFacilities';
import * as state from '~/modules/trade/_state/purchaseOrdersList.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SupplierFilter } from '~/shared/components/SupplierFilter';

const columns = [
  'documentNumber',
  'startDate',
  'endDate',
  'counterpartyReferenceNumber',
  'supplier',
  'lineItemCount',
  'allocated',
  'fulfilled',
  'netWeight',
  'status',
  'totalPrice',
  'createdOn',
];

const STATUS_COLUMN_KEYS: Record<PurchaseOrderStatus, string[]> = {
  Draft: columns,
  Open: columns,
  Closed: columns,
  Voided: columns,
};

export type CommonCellProps = {
  className: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
};

type Args = {
  status?: PurchaseOrderStatus;
  facilities?: FacilityRecord[];
  commonCellProps?: (purchaseOrder: PurchaseOrderRecord) => CommonCellProps;
  setIsLoadDialogOpen?: (open: boolean, purchaseOrderId?: string) => void;
  setSelectedPurchaseOrder?: (
    purchaseOrder: PurchaseOrderRecord | null,
  ) => void;
  onVoid?: (purchaseOrderId: string) => void;
  onClose?: (purchaseOrderId: string) => void;
  onRefetch?: () => void;
  supplierFilter?:
    | React.ReactNode
    | ((values: { close: () => void }) => React.ReactNode);
  isSupplierFilterActive?: boolean;
};

export type PurchaseOrderActionMenuProps = {
  purchaseOrder: PurchaseOrderRecord;
  setIsLoadDialogOpen?: (open: boolean, purchaseOrderId?: string) => void;
  setSelectedPurchaseOrder?: (
    purchaseOrder: PurchaseOrderRecord | null,
  ) => void;
  onVoid?: (purchaseOrderId: string) => void;
  onClose?: (purchaseOrderId: string) => void;
  onRefetch?: () => void;
};

const PurchaseOrderActionMenu = ({
  purchaseOrder,
  setIsLoadDialogOpen,
  setSelectedPurchaseOrder,
  onVoid,
  onRefetch,
  onClose,
}: PurchaseOrderActionMenuProps) => {
  const menuItems = usePurchaseOrderDetailMenuItems({
    purchaseOrder,
    showVoidButton:
      purchaseOrder.status === enumPurchaseOrderStatus.Open ||
      purchaseOrder.status === enumPurchaseOrderStatus.Draft,
    setIsLoadDialogOpen: (open: boolean, purchaseOrderId?: string) => {
      if (open && setSelectedPurchaseOrder) {
        setSelectedPurchaseOrder(purchaseOrder);
      }
      setIsLoadDialogOpen?.(open, purchaseOrderId);
    },
    onClose,
    onVoid: onVoid,
    onRefetch: onRefetch,
    displayListActions: true,
  });

  return (
    <div className="@PurchaseOrderActionMenu flex justify-center">
      <Menu
        trigger={
          <Button
            size="icon"
            icon={<LucideMoreHorizontal />}
            variant="outline"
          />
        }
        items={menuItems}
      />
    </div>
  );
};

export const usePurchaseOrdersListColumns = ({
  status,
  facilities = [],
  commonCellProps,
  setIsLoadDialogOpen,
  setSelectedPurchaseOrder,
  onVoid,
  onClose,
  onRefetch,
}: Args = {}) => {
  const t = useTranslations();

  const setOffset = useSetAtom(state.$offset);

  const [selectedSupplierIds, setSelectedSupplierIds] = useAtom(
    state.$selectedSupplierIds,
  );

  const selectedStatusesSet = useAtomValue(state.$columnSelectedStatuses);
  const columnDateRange = useAtomValue(state.$columnDateRange);
  const columnCreatedOnDateRange = useAtomValue(
    state.$columnCreatedOnDateRange,
  );

  const handleSupplierSelectionChange = React.useCallback(
    (supplierIds: Set<React.Key>) => {
      setSelectedSupplierIds(supplierIds as Set<string>);
    },
    [setSelectedSupplierIds],
  );

  const handleSupplierFilterReset = React.useCallback(() => {
    setSelectedSupplierIds(new Set());
    setOffset(0);
  }, [setSelectedSupplierIds, setOffset]);

  const ALL_COLUMNS: Record<
    string,
    DataGridColumn<PurchaseOrderRecord>
  > = React.useMemo(
    () => ({
      endDate: {
        key: 'endDate',
        header: t.purchaseOrdersView.table.columns.endDate,
        sortable: true,
        columnFilter: ({ close }) => <DateRangeColumnFilter onClose={close} />,
        isFilterActive: !!columnDateRange,
        cell: ({ row }) => {
          const content = row.endDate ? (
            <LocalizedDate date={row.endDate as AnyCalendarDate} />
          ) : (
            '-'
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      startDate: {
        key: 'startDate',
        header: t.purchaseOrdersView.table.columns.startDate,
        sortable: true,
        columnFilter: ({ close }) => <DateRangeColumnFilter onClose={close} />,
        isFilterActive: !!columnDateRange,
        cell: ({ row }) => {
          const content = row.startDate ? (
            <LocalizedDate date={row.startDate as AnyCalendarDate} />
          ) : (
            '-'
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      documentNumber: {
        key: 'documentNumber',
        header: t.purchaseOrdersView.table.columns.purchaseOrderNumber,
        sortable: true,
        cell: ({ row }) => {
          const content = row.documentNumber ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="truncate text-left"
            >
              {content}
            </div>
          ) : (
            <div className="truncate text-left">{content}</div>
          );
        },
      },
      counterpartyReferenceNumber: {
        key: 'counterpartyReferenceNumber',
        header: t.purchaseOrdersView.table.columns.counterpartySalesOrderNumber,
        cell: ({ row }) => {
          const content = row.counterpartyReferenceNumber ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="truncate text-left"
            >
              {content}
            </div>
          ) : (
            <div className="truncate text-left">{content}</div>
          );
        },
      },
      supplier: {
        key: 'supplier',
        header: t.purchaseOrdersView.table.columns.supplier,
        columnFilter: ({ close }) => (
          <SupplierFilter
            selectedSupplierIds={selectedSupplierIds}
            onSelectionChange={handleSupplierSelectionChange}
            onReset={handleSupplierFilterReset}
            onClose={close}
          />
        ),
        isFilterActive: selectedSupplierIds.size > 0,
        cell: ({ row }) => {
          const content = row.supplierName ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="truncate text-left"
            >
              {content}
            </div>
          ) : (
            <div className="truncate text-left">{content}</div>
          );
        },
      },
      facilityName: {
        key: 'facilityName',
        header: t.purchaseOrdersView.table.columns.facility,
        cell: ({ row }) => {
          const facility = row.facility?.id
            ? facilities.find((f) => f.id === row.facility.id)
            : null;
          const content = facility?.facilityName ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="truncate text-left"
            >
              {content}
            </div>
          ) : (
            <div className="truncate text-left">{content}</div>
          );
        },
      },
      lineItemCount: {
        key: 'lineItemCount',
        header: t.purchaseOrdersView.table.columns.numberOfMaterials,
        cell: ({ row }) => {
          const content = row.lineItemCount ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="text-right"
            >
              {content}
            </div>
          ) : (
            <div className="text-right">{content}</div>
          );
        },
      },
      allocated: {
        key: 'allocated',
        header: t.purchaseOrdersView.table.columns.allocated,
        cell: ({ row }) => (
          <div
            {...commonCellProps?.(row as PurchaseOrderRecord)}
            className="text-right"
          >
            {row.allocatedPercentage != null
              ? `${Math.round(row.allocatedPercentage)}%`
              : '-'}
          </div>
        ),
      },
      fulfilled: {
        key: 'fulfilled',
        header: t.purchaseOrdersView.table.columns.fulfilled,
        cell: ({ row }) => (
          <div
            {...commonCellProps?.(row as PurchaseOrderRecord)}
            className="text-right"
          >
            {row.fulfilledPercentage != null
              ? `${Math.round(row.fulfilledPercentage)}%`
              : '-'}
          </div>
        ),
      },
      netWeight: {
        key: 'netWeight',
        header: t.purchaseOrdersView.table.columns.netWeight,
        cell: ({ row }) => (
          <NetWeightCell row={row} commonCellProps={commonCellProps} />
        ),
      },
      status: {
        key: 'status',
        header: t.purchaseOrdersView.table.columns.status,
        sortable: true,
        columnFilter: ({ close }) => <StatusColumnFilter onClose={close} />,
        isFilterActive: selectedStatusesSet.size > 0,
        cell: ({ row }) => {
          const content = !row.status ? (
            '-'
          ) : (
            <PurchaseOrderStatusBadge status={row.status} />
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      totalPrice: {
        key: 'totalPrice',
        header: t.purchaseOrdersView.table.columns.total,
        cell: ({ row }) => {
          const total = row.totalPrice?.amount;
          const content = total ? `$${total.toLocaleString()}` : '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="text-right"
            >
              {content}
            </div>
          ) : (
            <div className="text-right">{content}</div>
          );
        },
      },
      createdOn: {
        key: 'createdOn',
        header: t.purchaseOrdersView.table.columns.createdOn,
        sortable: true,
        columnFilter: ({ close }) => (
          <CreatedOnDateRangeColumnFilter onClose={close} />
        ),
        isFilterActive: !!columnCreatedOnDateRange,
        cell: ({ row }) => {
          const content = row.timestamps?.createdAt ? (
            <LocalizedDate date={row.timestamps.createdAt as AnyCalendarDate} />
          ) : (
            '-'
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as PurchaseOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      actions: {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => {
          const purchaseOrder = row as PurchaseOrderRecord;
          return (
            <PurchaseOrderActionMenu
              purchaseOrder={purchaseOrder}
              setIsLoadDialogOpen={(open: boolean) => {
                setIsLoadDialogOpen?.(open, purchaseOrder.id);
              }}
              setSelectedPurchaseOrder={setSelectedPurchaseOrder}
              onVoid={onVoid}
              onClose={onClose}
              onRefetch={onRefetch}
            />
          );
        },
      },
    }),
    [
      t,
      commonCellProps,
      selectedSupplierIds,
      handleSupplierSelectionChange,
      handleSupplierFilterReset,
      selectedStatusesSet,
      columnDateRange,
      columnCreatedOnDateRange,
      facilities,
      setSelectedPurchaseOrder,
      onVoid,
      onRefetch,
      setIsLoadDialogOpen,
      onClose,
    ],
  );

  const selectedStatus = status || enumPurchaseOrderStatus.Open;
  const visibleColumnKeys = STATUS_COLUMN_KEYS[selectedStatus];

  return React.useMemo(() => {
    const baseColumns = visibleColumnKeys
      .map((key) => ALL_COLUMNS[key])
      .filter(Boolean);
    return [...baseColumns, ALL_COLUMNS.actions];
  }, [ALL_COLUMNS, visibleColumnKeys]);
};
